import * as React from "react";
import type { NavigationBarProps, NavLink } from "../../../../../ui/components/NavigationBar";
import * as brandStyles from "../../../../../ui/components/AuthBrand/AuthBrand.style.css";
import type { User } from "../../../../lib/api/users";
import type { NavigationModule } from "../../../../lib/api/navigation";
import { resolveNavIcon } from "./navIcons";
import {
  findModuleSlugForPathname,
  isViewActive,
} from "../../../../lib/navigation/route-matching";
import { ACTIVE_MODULE_STORAGE_KEY } from "../../../../lib/navigation/constants";
import { isE2eTestingAvailable } from "../../../../lib/auth/app-origins";

export const lionProBrand = {
  mark: (
    <img
      src="/logos/lion_energy_lion.svg"
      alt=""
      width={24}
      height={24}
      className={brandStyles.logo}
    />
  ),
  name: (
    <img
      src="/logos/lion_energy_wordmark.png"
      srcSet="/logos/lion_energy_wordmark@2x.png 2x, /logos/lion_energy_wordmark@3x.png 3x"
      alt="Lion Energy"
      className={brandStyles.wordmark}
      style={{ width: "100%", height: "auto" }}
    />
  ),
};

function userInitials(user: User): string {
  const a = user.firstName?.[0] ?? "";
  const b = user.lastName?.[0] ?? "";
  const initials = (a + b).toUpperCase();
  return initials || user.email[0]?.toUpperCase() || "?";
}

function buildViewLinks(
  mod: NavigationModule,
  pathname: string,
  navigate: (to: string) => void,
): NavLink[] {
  const e2eAvailable = isE2eTestingAvailable();
  return mod.views
    .filter((view) => (view.slug === "e2e" ? e2eAvailable : true))
    .map((view) => {
      const patterns =
        view.routePatterns.length > 0
          ? view.routePatterns
          : [view.defaultPath];
      return {
        label: view.label,
        icon: resolveNavIcon(view.icon),
        to: view.defaultPath,
        viewSlug: view.slug,
        routePatterns: patterns,
        active: isViewActive(pathname, patterns),
        onClick: () => navigate(view.defaultPath),
      };
    });
}

export function resolveActiveModuleSlug(
  navigation: User["navigation"],
  pathname: string,
  fallbackSlug?: string | null,
): string | null {
  if (!navigation.modules.length) return null;

  const fromPath = findModuleSlugForPathname(navigation, pathname);
  if (fromPath) return fromPath;

  if (fallbackSlug && navigation.modules.some((m) => m.slug === fallbackSlug)) {
    return fallbackSlug;
  }

  try {
    const stored = sessionStorage.getItem(ACTIVE_MODULE_STORAGE_KEY);
    if (stored && navigation.modules.some((m) => m.slug === stored)) {
      return stored;
    }
  } catch {
    /* ignore */
  }

  return navigation.modules[0]?.slug ?? null;
}

export interface BuildNavigationBarOptions {
  user: User;
  activeModuleSlug: string | null;
  pathname: string;
  onModuleChange: (slug: string) => void;
  onNavigate: (to: string) => void;
  onLogout: () => void;
  onChangeLanguageClick?: () => void;
  onUnverifiedEmailClick?: () => void;
  orgContext?: import("../../../../../ui/components/NavigationBar/NavigationBar.types").NavOrgContext;
  labels?: {
    profile?: string;
    changeLanguage?: string;
    logOut?: string;
    languageName?: string;
    languageFlagUrl?: string | null;
  };
}

export function buildNavigationBarProps({
  user,
  activeModuleSlug,
  pathname,
  onModuleChange,
  onNavigate,
  onLogout,
  onChangeLanguageClick,
  onUnverifiedEmailClick,
  orgContext,
  labels,
}: BuildNavigationBarOptions): Omit<NavigationBarProps, "open" | "onOpenChange" | "onWidthChange"> {
  // Technician Certification is its own app; hide the legacy top-level module in Pro.
  const modules = user.navigation.modules.filter((m) => m.slug !== "certification");
  const navigation = { ...user.navigation, modules };
  const products = modules.map((mod) => ({
    id: mod.slug,
    label: mod.label,
    icon: resolveNavIcon(mod.icon),
    description: mod.description ?? undefined,
  }));

  const activeModule =
    modules.find((m) => m.slug === activeModuleSlug) ?? modules[0];

  const links = activeModule
    ? buildViewLinks(activeModule, pathname, onNavigate)
    : [];

  return {
    brand: lionProBrand,
    products,
    activeProductId: activeModule?.slug,
    onProductChange: (id) => {
      if (id === activeModuleSlug) return;

      try {
        sessionStorage.setItem(ACTIVE_MODULE_STORAGE_KEY, id);
      } catch {
        /* ignore */
      }
      onModuleChange(id);

      const mod = modules.find((m) => m.slug === id);
      const firstView = mod?.views[0];
      if (firstView?.defaultPath) {
        onNavigate(firstView.defaultPath);
      }
    },
    links,
    subLinks: [],
    user: {
      name: `${user.firstName} ${user.lastName}`.trim() || user.email,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      avatarUrl: user.avatarUrl,
      emailVerified: user.emailVerified,
      initials: userInitials(user),
      onUnverifiedEmailClick,
      onAccountClick: () => onNavigate("/profile"),
      onChangeLanguageClick,
      onLogoutClick: onLogout,
      profileLabel: labels?.profile,
      changeLanguageLabel: labels?.changeLanguage,
      logOutLabel: labels?.logOut,
      languageName: labels?.languageName,
      languageFlagUrl: labels?.languageFlagUrl,
    },
    orgContext,
  };
}
